import { isEqual } from "lodash";
import React, { createContext, useCallback, useMemo, useState } from "react";

// Define the shape of the context value
interface FilterContextType {
  filters: Record<string, object>;
  updateFilter: (page: string, newFilter: object) => void;
  getFiltersForPath: (pathname: string) => object;
}

// Create a Context for filters with a default value
const FilterContext = createContext<FilterContextType>({
  filters: {}, // Default empty filters object
  updateFilter: () => undefined, // Default no-op function
  getFiltersForPath: () => ({}), // Default no-op function
});

export function useFilterContext() {
  const context = React.useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext can only be used inside FilterProvider");
  }
  return context;
}

export const FilterProvider = ({ children }: { children: React.ReactNode }) => {
  // Local state to store filters
  const [filters, setFilters] = useState<Record<string, object>>({});

  // Identity has to be stable: consumers list this in effect dependency arrays,
  // and a fresh function each render meant the effect re-ran, wrote a filter,
  // re-rendered this provider, and ran again — an unbounded render loop.
  const updateFilter = useCallback((page: string, newFilter: object) => {
    setFilters((prevFilters) => {
      // Rewriting the same values still produced a new object, so React saw a
      // state change every time and never settled. Bail out instead. isEqual
      // rather than a shallow compare: filter payloads already carry nested
      // values (CampaignStats watches a date `range` object), which a reference
      // check would treat as changed on every render.
      if (isEqual(prevFilters[page], newFilter)) return prevFilters;
      return { ...prevFilters, [page]: newFilter };
    });
  }, []);

  const getFiltersForPath = useCallback(
    (pathname: string) => filters[pathname] || {},
    [filters]
  );

  const value = useMemo(
    () => ({ filters, updateFilter, getFiltersForPath }),
    [filters, updateFilter, getFiltersForPath]
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};
